import React, { useEffect, useState, useCallback } from 'react';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';
import {
    v4_fetch_mailbox_recipients,
    v4_add_mailbox_recipients,
    v4_mailbox_configuration,
    v4_mailbox_emails,
} from '../../../actions/v4/mailBox';
import AsyncSelect from 'react-select/async';
import {
    v3_ncr_send_report_mail,
} from '../../../actions/v3/ncr';
import {
    buildAdhocRecipientOption,
    filterRecipientOption,
    mapRecipientOptions,
    mergeRecipientOptions,
    normalizeRecipientEmail,
} from '../../MailBox/recipientSelectUtils';
import { buildCommentReportName } from '../../../utils';

const getInitialMailState = (emailOptions = [], ccOptions = [], subjectValue = '') => ({
    fromEmailId: { name: 'fromEmailId', value: '', error: '', isRequired: true },
    replyMail: { name: 'replyMail', value: null, error: '', isRequired: false },
    email: { name: 'email', value: [], error: '', options: emailOptions, isRequired: true },
    cc: { name: 'cc', value: [], options: ccOptions, error: '', isRequired: false },
    subject: { name: 'subject', value: subjectValue, error: '', isRequired: true },
    body: { name: 'body', value: '', error: '', isRequired: false },
    reportType: { name: 'reportType', value: 'pdf', error: '', isRequired: false },
});

const SendReport = ({ data, modalId = 'sendMail' }) => {
    const [mailBoxes, setMailBox] = useState([]);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const ncrId = data?.ncrId;
    const autoSubject = buildCommentReportName(
        'NCR',
        data?.ncrNumber ?? data?.NCR_NUMBER,
        data?.subject ?? data?.SUBJECT
    );
    const [state, setState] = useState(getInitialMailState([], [], autoSubject));

    // Fetch mailbox configurations
    useEffect(() => {
        v4_mailbox_configuration().then((res) => {
            if (res?.status) setMailBox(res.result);
        });
    }, []);

    // Fetch recipient list
    useEffect(() => {
        v4_fetch_mailbox_recipients().then((res) => {
            if (res?.status) {
                const emailOptions = mapRecipientOptions(res.result);

                setState((prev) => ({
                    ...prev,
                    email: { ...prev.email, options: emailOptions },
                    cc: { ...prev.cc, options: emailOptions },
                }));
            }
        });
    }, []);

    // Fetch PDF for preview
    useEffect(() => {
        if (!ncrId) return;
        let urlObject = null;

        const fetchPdf = async () => {
            setLoading(true);
            try {

                const response = await fetch(`${process.env.REACT_APP_BASE_URL}/v3/ncr/download/pdf/${ncrId}`);
                if (!response.ok) throw new Error('Failed to download PDF');

                const blob = await response.blob();
                urlObject = URL.createObjectURL(blob);
                setPdfUrl(urlObject);
            } catch (err) {
                console.error('PDF Preview Error:', err);
                toast.error('Failed to load PDF preview.');
            } finally {
                setLoading(false);
            }
        };

        fetchPdf();

        return () => {
            if (urlObject) URL.revokeObjectURL(urlObject);
        };
    }, [ncrId]);

    // Handle input and select changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setState((prev) => {
            const nextState = {
                ...prev,
                [name]: { ...prev[name], value, error: '' },
            };
            if (name === 'fromEmailId') {
                nextState.replyMail = { ...nextState.replyMail, value: null };
            }
            return nextState;
        });
    };

    const handleMultiSelectChange = (selectedOptions, name) => {
        setState((prev) => ({
            ...prev,
            [name]: { ...prev[name], value: selectedOptions || [], error: '' },
        }));
    };

    const handleCreateOption = async (inputValue, name) => {
        const email = normalizeRecipientEmail(inputValue);
        if (!email) return;

        const exists = (state.email.options || []).some((opt) => opt.value === email);
        const newOption = buildAdhocRecipientOption(email);
        if (!newOption) return;

        setState((prev) => {
            const nextValue = Array.isArray(prev[name].value) ? prev[name].value : [];
            const alreadySelected = nextValue.some((opt) => opt.value === email);
            return {
                ...prev,
                [name]: {
                    ...prev[name],
                    value: alreadySelected ? nextValue : [...nextValue, newOption],
                    error: '',
                },
                email: { ...prev.email, options: mergeRecipientOptions(prev.email.options, [newOption]) },
                cc: { ...prev.cc, options: mergeRecipientOptions(prev.cc.options, [newOption]) },
            };
        });

        if (!exists) {
            try {
                await v4_add_mailbox_recipients({
                    email,
                    name: email.includes('@') ? email.split('@')[0] : email,
                });
            } catch (err) {
                toast.error('Failed to save new email for suggestions.');
            }
        }
    };

    const loadReplyEmails = async (inputValue) => {
        if (!state.fromEmailId.value) return [];
        try {
            const res = await v4_mailbox_emails(1, 20, 'INBOX', state.fromEmailId.value, inputValue);
            if (res?.status && res.result?.mails) {
                return res.result.mails.map(mail => ({
                    value: mail.messageId,
                    label: `[${mail.date ? new Date(mail.date).toLocaleDateString() : ''}] ${mail.subject || '(No Subject)'}`,
                    mail
                }));
            }
        } catch (error) {
            console.error('Failed to fetch reply emails', error);
        }
        return [];
    };

    const handleReplyMailChange = (selectedOption) => {
        setState(prev => {
            const nextState = {
                ...prev,
                replyMail: { ...prev.replyMail, value: selectedOption }
            };
            if (selectedOption?.mail?.subject) {
                const sub = selectedOption.mail.subject;
                const newSubject = /^re:/i.test(sub) ? sub : `Re: ${sub}`;
                nextState.subject = { ...nextState.subject, value: newSubject };
            }
            return nextState;
        });
    };

    const validateForm = useCallback(() => {
        let valid = true;
        const updated = { ...state };

        Object.keys(state).forEach((key) => {
            const value = state[key].value;
            const isMissing = Array.isArray(value)
                ? value.length === 0
                : value === null || value === undefined || String(value).trim() === '';

            if (state[key].isRequired && isMissing) {
                updated[key].error = 'This field is required';
                valid = false;
            }
        });

        setState(updated);
        if (!valid) toast.error('Please fill all required fields.');
        return valid;
    }, [state]);

    const resetMailState = useCallback(() => {
        setState((prev) => getInitialMailState(prev.email.options, prev.cc.options, autoSubject));
        setShowPreview(false);
        setPdfUrl(null);
    }, [autoSubject]);

    useEffect(() => {
        resetMailState();
    }, [ncrId, resetMailState]);

    useEffect(() => {
        if (!autoSubject) return;

        setState((prev) => {
            if (prev.subject.value) return prev;
            return {
                ...prev,
                subject: {
                    ...prev.subject,
                    value: autoSubject,
                },
            };
        });
    }, [autoSubject]);

    const handleSubmit = async () => {
        if (!ncrId) {
            toast.error('NCR details not found.');
            return;
        }

        if (!validateForm()) return;

        setSending(true);

        const toEmails = state.email.value.map((e) => e.value);
        const ccEmails = state.cc.value.map((e) => e.value);
        const subject = state.subject.value || autoSubject;

        const payload = {
            to: toEmails,
            cc: ccEmails,
            subject,
            html: state.body.value,
            id: state.fromEmailId.value,
            ncrId,
            inspectionId: data?.inspectionId,
            planApprovalId: data?.planApprovalId,
            reportType: state.reportType.value,
            replyToMessageId: state.replyMail?.value?.value || null,
            replyToReferences: state.replyMail?.value?.mail?.references || state.replyMail?.value?.value || null,
        };

        try {
            let apiFn;
            apiFn = v3_ncr_send_report_mail;
            const res = await apiFn(payload);

            if (res?.status) {
                toast.success('Mail sent successfully!');
                resetMailState();
                const modal = window.bootstrap.Modal.getInstance(document.getElementById(modalId));
                if (modal) modal.hide();
            } else {
                toast.error(res?.error || 'Mail sending failed.');
            }
        } catch (err) {
            toast.error('Error while sending mail.');
        } finally {
            setSending(false);
        }
    };

    return (
        <React.Fragment>
            <div className="row">
                <div>
                    {/* From */}
                    <div className="mb-3">
                        <select
                            name="fromEmailId"
                            value={state.fromEmailId.value}
                            onChange={handleInputChange}
                            className="form-select"
                        >
                            <option value="">Send From</option>
                            {mailBoxes?.map((mail) => (
                                <option key={mail.ID} value={mail.ID}>
                                    {mail.EMAIL}
                                </option>
                            ))}
                        </select>
                        {state.fromEmailId.error && <small className="text-danger">{state.fromEmailId.error}</small>}
                    </div>
                    {state.fromEmailId.value && (
                        <div className="mb-3">
                            <label className="form-label text-muted mb-1" style={{ fontSize: '0.85rem' }}>Reply to Email Thread (Optional)</label>
                            <AsyncSelect
                                cacheOptions
                                defaultOptions
                                loadOptions={loadReplyEmails}
                                onChange={handleReplyMailChange}
                                value={state.replyMail.value}
                                placeholder="Search emails to reply to..."
                                isClearable
                            />
                        </div>
                    )}

                    {/* To */}
                    <div className="mb-3">
                        <CreatableSelect
                            isMulti
                            name="email"
                            value={state.email.value}
                            options={state.email.options}
                            filterOption={filterRecipientOption}
                            onChange={(e) => handleMultiSelectChange(e, 'email')}
                            onCreateOption={(value) => handleCreateOption(value, 'email')}
                            placeholder="To"
                            isClearable
                        />
                        {state.email.error && <small className="text-danger">{state.email.error}</small>}
                    </div>

                    {/* CC */}
                    {state.email.value.length > 0 && (
                        <div className="mb-3">
                            <CreatableSelect
                                isMulti
                                name="cc"
                                value={state.cc.value}
                                options={state.cc.options}
                                filterOption={filterRecipientOption}
                                onChange={(e) => handleMultiSelectChange(e, 'cc')}
                                onCreateOption={(value) => handleCreateOption(value, 'cc')}
                                placeholder="CC"
                                isClearable
                            />
                            {state.cc.error && <small className="text-danger">{state.cc.error}</small>}
                        </div>
                    )}
                    <div className="mb-3">
                        <select
                            name="reportType"
                            value={state.reportType.value}
                            onChange={handleInputChange}
                            className="form-select"
                        >
                            <option value="pdf">PDF</option>
                            <option value="docx">DOC</option>
                        </select>
                        {state.reportType.error && <small className="text-danger">{state.reportType.error}</small>}
                    </div>

                    {/* Subject */}
                    <div className="mb-3">
                        <input
                            type="text"
                            className="form-control"
                            name="subject"
                            value={state.subject.value}
                            onChange={handleInputChange}
                            placeholder="Subject"
                        />
                        {state.subject.error && <small className="text-danger">{state.subject.error}</small>}
                    </div>

                    {/* Body */}
                    <div className="mb-3">
                        <textarea
                            className="form-control"
                            rows="5"
                            name="body"
                            value={state.body.value}
                            onChange={handleInputChange}
                            placeholder="Write your message..."
                        />
                        {state.body.error && <small className="text-danger">{state.body.error}</small>}
                    </div>
                </div>
            </div>

            {/* Buttons */}

            {/* PDF Preview Modal */}

            {showPreview == true && <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Preview</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => { setShowPreview(false) }}
                        />
                    </div>

                    <div
                        className="modal-body d-flex justify-content-center align-items-center"
                        style={{ height: '80vh' }}
                    >
                        {loading ? (
                            <div style={{ fontSize: '18px', fontWeight: 500, color: '#555' }}>Loading...</div>
                        ) : pdfUrl ? (
                            <iframe
                                src={pdfUrl}
                                title="PDF Preview"
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                            />
                        ) : (
                            <p className="text-muted">No preview available</p>
                        )}
                    </div>
                </div>
            </div>}
            <div className="mt-3 d-flex gap-2">
                <button
                    className="btn btn-success"
                    type="button"
                    disabled={sending}
                    onClick={handleSubmit}
                >
                    {sending ? 'Sending...' : 'Send'}
                </button>

                <button
                    type="button"
                    className="btn btn-danger text-white"
                    onClick={() => {
                        resetMailState();
                        const modal = window.bootstrap.Modal.getInstance(document.getElementById(modalId));
                        if (modal) modal.hide();
                    }}
                >
                    Cancel
                </button>

                <button
                    className="btn btn-outline-primary"
                    style={{ borderColor: 'black' }}
                    onClick={() => { setShowPreview(true) }}
                >
                    Preview
                </button>
            </div>


        </React.Fragment>
    );
};

export default SendReport;
